import { Fragment, useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { getComprasBandeja, getComprasPaquetes, separarSolicitudCompra, reincorporarSolicitudCompra, cargarEnSapCompras } from '../api/client'
import { currentLocale } from '../i18n'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'

// Compras v2 > Bandeja de compras (brief §12): what the purchasing desk has to do, by task.
// What needs the link request <-> order, or the masters, arrives with those pieces.

const TAREAS = ['por_cargar', 'antiguos', 'sin_proyecto', 'sin_enlazar']
const num = (v, max = 0) => new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: max }).format(v)
const clp = (v) => (v == null ? '—' : `$${num(v)}`)

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(currentLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto)
  } catch {
    const ta = document.createElement('textarea')            // where the clipboard API is not allowed
    ta.value = texto
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
}

function BotonCopiar({ texto, children, doneLabel }) {
  const [hecho, setHecho] = useState(false)
  return (
    <button type="button" onClick={async () => { await copiar(texto); setHecho(true); setTimeout(() => setHecho(false), 2000) }}
      className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
      {hecho ? `✓ ${doneLabel}` : children}
    </button>
  )
}

function Dias({ d, t }) {
  if (d == null) return '—'
  return <span className={d >= 7 ? 'text-amber-700 font-medium' : ''}>{d === 0 ? t('days.today') : t('days.ago', { count: d })}</span>
}

// The packages for the ticked requests: one per supplier and currency, ready to paste into the SAP order.
function Paquetes({ paquetes, onClose, t }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-5" role="dialog" aria-label={t('package.title')}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('package.title')}</h2>
            <p className="text-sm text-gray-500">{t('package.intro')}</p>
          </div>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800">{t('package.close')} ✕</button>
        </div>
        {paquetes.length === 0 && <p className="text-sm text-gray-500">{t('package.empty')}</p>}
        {paquetes.map((g, i) => (
          <section key={i} className="border rounded-lg">
            <header className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium text-gray-900">{g.proveedor || t('package.noSupplier')} <span className="text-gray-400 font-normal">· {g.moneda}</span></div>
                <div className="text-xs text-gray-500">{t('package.requests', { list: g.solicitudes.map(id => `#${id}`).join(', ') })} · {t('package.lines', { count: g.filas.length })}</div>
              </div>
              <div className="flex gap-2">
                <BotonCopiar texto={g.comentario} doneLabel={t('package.copied')}>{t('package.copyComment')}</BotonCopiar>
                <BotonCopiar texto={g.texto} doneLabel={t('package.copied')}>{t('package.copyLines')}</BotonCopiar>
              </div>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-gray-500 bg-white">
                  <tr>{['item', 'description', 'quantity', 'price', 'discount', 'tax', 'project', 'area'].map(c => <th key={c} className="px-3 py-1.5 font-medium">{t(`package.cols.`)}</th>)}</tr>
                </thead>
                <tbody>
                  {g.filas.map((f, j) => (
                    <tr key={j} className="border-t">
                      <td className="px-3 py-1.5 font-mono">{f.item || '—'}</td>
                      <td className="px-3 py-1.5">{f.descripcion}</td>
                      <td className="px-3 py-1.5">{f.cantidad}{f.unidad ? ` ${f.unidad}` : ''}</td>
                      <td className="px-3 py-1.5">{f.precio ?? '—'}</td>
                      <td className="px-3 py-1.5">{f.pct_dcto ?? 0}</td>
                      <td className="px-3 py-1.5 font-mono">{f.indicador}</td>
                      <td className="px-3 py-1.5 font-mono">{f.proyecto || '—'}</td>
                      <td className="px-3 py-1.5">{f.area || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        <p className="text-xs text-gray-400">{t('package.note')}</p>
      </div>
    </div>
  )
}

// The joint purchases as seen under the Priority Queue's cut: only what is above it, only what is below, or all.
function vistaPorCorte(compras, vista) {
  if (vista === 'todas') return compras
  const quiere = vista === 'sobre'
  return compras
    .map(c => {
      const sols = c.solicitudes.filter(s => !!s.sobre_corte === quiere)
      return { ...c, solicitudes: sols, n_solicitudes: sols.length, n_lineas: sols.reduce((a, s) => a + s.lineas.length, 0),
        proyectos: [...new Set(sols.map(s => s.proyecto).filter(Boolean))].sort() }
    })
    .filter(c => c.solicitudes.length)
}

export default function ComprasBandeja() {
  const { t } = useTranslation(['compras', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const esAdmin = (user?.roles || []).includes('admin')
  const [sp, setSp] = useSearchParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [fuera, setFuera] = useState(() => new Set())      // unticked requests: the joint purchase includes all by default
  const zoom = useZoom('adq_zoom_bandeja')
  const [paquetes, setPaquetes] = useState(null)
  const [ocupada, setOcupada] = useState(false)
  const [separando, setSeparando] = useState(null)         // { id, motivo }
  const [po, setPo] = useState({})                         // the SAP order number typed, per purchase
  const [vistaCorte, setVistaCorte] = useState(null)         // 'sobre' | 'bajo' | 'todas'; null: above the cut when there is one

  const cargar = useCallback(() => getComprasBandeja().then(setData).catch(e => setErr(e.response?.data?.detail || e.message)), [])

  useEffect(() => {
    let vivo = true
    getComprasBandeja().then(r => { if (vivo) setData(r) }).catch(e => { if (vivo) setErr(e.response?.data?.detail || e.message) })
    return () => { vivo = false }
  }, [])

  const tareas = data?.tareas
  const hayCorte = !!tareas?.por_cargar.hay_corte
  const vistaActual = vistaCorte ?? (hayCorte ? 'sobre' : 'todas')
  const conteoCorte = {
    sobre: (tareas?.por_cargar.items || []).filter(s => s.sobre_corte).length,
    bajo: (tareas?.por_cargar.items || []).filter(s => s.sobre_corte === false).length,
  }
  const comprasVista = tareas ? vistaPorCorte(tareas.por_cargar.compras, hayCorte ? vistaActual : 'todas') : []
  const tareaUrl = sp.get('tarea')
  // The first task with something to do, until one is chosen.
  const tarea = TAREAS.includes(tareaUrl) ? tareaUrl : (tareas ? (TAREAS.find(k => tareas[k].n > 0) || 'por_cargar') : 'por_cargar')
  const elegir = (k) => setSp(new URLSearchParams({ tarea: k }), { replace: true })

  const incluidas = (c) => c.solicitudes.map(s => s.id).filter(id => !fuera.has(id))
  const alternar = (id) => setFuera(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  const correr = async (fn, falla) => {
    setOcupada(true); setErr('')
    try { await fn(); await cargar() } catch (e) { setErr(e.response?.data?.detail || falla) }
    setOcupada(false)
  }
  const armarPaquete = (c) => correr(async () => { setPaquetes((await getComprasPaquetes(incluidas(c))).paquetes) }, t('package.failed'))
  const separar = () => correr(async () => { await separarSolicitudCompra(separando.id, separando.motivo.trim()); setSeparando(null) }, t('joint.separateFailed'))
  const reincorporar = (id) => correr(() => reincorporarSolicitudCompra(id), t('joint.rejoinFailed'))
  const marcarCargadas = (c) => correr(async () => {
    const r = await cargarEnSapCompras(incluidas(c), po[c.clave])
    if (r.omitidas?.length) setErr(t('joint.skipped', { count: r.omitidas.length }))
  }, t('joint.loadFailed'))

  const dias = data?.dias_habiles_sin_importar
  const imp = data?.importacion

  const tarjeta = (k, n, extra) => (
    <button key={k} type="button" onClick={() => elegir(k)} aria-pressed={tarea === k}
      className={`text-left rounded-xl border px-4 py-3 ${tarea === k ? 'border-gray-900 bg-white shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
      <div className="text-xs font-medium text-gray-500">{t(`tray.tasks.${k}`)}</div>
      <div className={`text-2xl font-semibold ${n > 0 ? 'text-gray-900' : 'text-gray-300'}`}>{num(n)}</div>
      {extra && <div className="text-xs text-gray-400">{extra}</div>}
    </button>
  )

  const tabla = (cols, filas) => (
    <div className="bg-white rounded-xl border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
          <tr>{cols.map(c => <th key={c.k} className={`px-3 py-2 ${c.right ? 'text-right' : ''}`}>{t(`tray.cols.${c.k}`)}</th>)}</tr>
        </thead>
        <tbody>{filas}</tbody>
      </table>
    </div>
  )
  const abrirPedido = (id) => navigate(`/compras/pedidos?abiertos=0&pedido=${id}`)
  const filaPedido = (p, extra) => (
    <tr key={p.pedido_id} onClick={() => abrirPedido(p.pedido_id)} className="border-t hover:bg-indigo-50/40 cursor-pointer">
      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{p.pedido_num}</td>
      <td className="px-3 py-2 max-w-[22rem]"><div className="truncate" title={p.descripcion}>{p.descripcion}</div>{p.n_lineas > 1 && <div className="text-xs text-gray-400">{t('moreLines', { n: p.n_lineas - 1 })}</div>}</td>
      <td className="px-3 py-2 text-gray-700 max-w-[14rem] truncate" title={p.proveedor}>{p.proveedor || '—'}</td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{p.proyectos.length ? p.proyectos.join(', ') : '—'}</td>
      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(p.fecha)} <span className="text-xs text-gray-400">· <Dias d={p.dias} t={t} /></span></td>
      {extra}
      <td className="px-3 py-2 text-right whitespace-nowrap">{clp(p.total_clp)}</td>
    </tr>
  )

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('tray.title')}</h1>
          <p className="text-sm text-gray-500">{t('tray.subtitle')}</p>
        </div>
        <ControlZoom z={zoom} />
      </header>

      {err && <p className="text-sm text-red-600">{err}</p>}

      {data && (
        <div className={`rounded-lg px-4 py-2 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${!imp || dias >= 1 ? 'bg-amber-50 text-amber-800' : 'bg-sky-50 text-sky-800'}`}>
          {!imp ? <span>{t('banner.never')}</span> : dias >= 1 ? (
            <span><strong>{t('tray.stale', { count: dias })}</strong> {t('tray.staleHint')}</span>
          ) : (
            <span><strong>{t('banner.dataAt', { date: fmtDate(imp.datos_al) })}</strong> · {t('banner.loaded', { when: fmtDateTime(imp.creada_en), who: imp.usuario || '—' })}</span>
          )}
          <Link to="/compras/pedidos" className="underline font-medium">{t('tray.goImport')}</Link>
        </div>
      )}

      <ZonaZoom z={zoom} libre>
      {tareas && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {tarjeta('por_cargar', tareas.por_cargar.n)}
            {tarjeta('antiguos', tareas.antiguos.n, clp(tareas.antiguos.total_clp))}
            {tarjeta('sin_proyecto', tareas.sin_proyecto.n, clp(tareas.sin_proyecto.total_clp))}
            {tarjeta('sin_enlazar', tareas.sin_enlazar.n)}
          </div>

          <p className="text-sm text-gray-500">{t(`tray.hint.${tarea}`, { days: tareas.antiguos.dias_umbral })}</p>

          {tarea === 'por_cargar' && (
            tareas.por_cargar.n === 0 ? <p className="text-sm text-gray-400 py-6 text-center">{t('tray.emptyPorCargar')}</p> : (
              <div className="space-y-4">
                {hayCorte && (
                  <div className="flex flex-wrap items-center gap-2 text-sm" role="group" aria-label={t('joint.cut.title')}>
                    <span className="text-gray-500">✂ {t('joint.cut.title')}:</span>
                    {[['sobre', conteoCorte.sobre], ['bajo', conteoCorte.bajo], ['todas', conteoCorte.sobre + conteoCorte.bajo]].map(([k, n]) => (
                      <button key={k} type="button" aria-pressed={vistaActual === k} onClick={() => setVistaCorte(k)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${vistaActual === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                        {t(`joint.cut.${k}`)} ({n})
                      </button>
                    ))}
                    <span className="text-xs text-gray-400">{t('joint.cut.hint')}</span>
                  </div>
                )}
                {comprasVista.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">{t('joint.cut.empty')}</p>}
                {comprasVista.map(c => (
                  <CompraConjunta key={c.clave} c={c} fuera={fuera} alternar={alternar} ocupada={ocupada} t={t}
                    po={po[c.clave] || ''} setPo={v => setPo(p => ({ ...p, [c.clave]: v }))}
                    separando={separando} setSeparando={setSeparando} separar={separar}
                    onPaquete={() => armarPaquete(c)} onCargadas={() => marcarCargadas(c)} />
                ))}
                {tareas.por_cargar.separadas.length > 0 && (
                  <section className="bg-white rounded-xl border">
                    <header className="px-4 py-3 border-b bg-gray-50">
                      <div className="font-medium text-gray-900">{t('joint.separatedTitle')} <span className="text-gray-400 font-normal">· {tareas.por_cargar.separadas.length}</span></div>
                      <div className="text-xs text-gray-500">{t('joint.separatedHint')}</div>
                    </header>
                    <table className="w-full text-sm">
                      <tbody>
                        {tareas.por_cargar.separadas.map(s => (
                          <tr key={s.id} className="border-t first:border-t-0">
                            <td className="px-3 py-2 whitespace-nowrap"><a href={`/purchases/${s.id}`} target="_blank" rel="noreferrer" className="font-medium text-indigo-700 hover:underline">#{s.numero}</a>{s.sobre_corte != null && <span className={`ml-2 text-[10px] ${s.sobre_corte ? "text-emerald-700" : "text-gray-400"}`}>{s.sobre_corte ? t("joint.cut.above") : t("joint.cut.below")}</span>}</td>
                            <td className="px-3 py-2 text-gray-700">{s.proveedor || t('package.noSupplier')}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{s.proyecto || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{s.separada_motivo}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button type="button" disabled={ocupada} onClick={() => reincorporar(s.id)} className="text-sm text-indigo-600 hover:underline disabled:opacity-40">{t('joint.rejoin')}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                )}
              </div>
            )
          )}

          {tarea === 'antiguos' && (
            tareas.antiguos.n === 0 ? <p className="text-sm text-gray-400 py-6 text-center">{t('tray.emptyAntiguos')}</p> : (
              <>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <Link to={`/compras/pedidos?abiertos=1&hasta=${tareas.antiguos.hasta}&sort=fecha&dir=asc`} className="text-indigo-600 hover:underline">{t('tray.seeInOrders')}</Link>
                  <span className="text-gray-400">{t('tray.shown', { shown: tareas.antiguos.items.length, total: tareas.antiguos.n })}</span>
                </div>
                {tabla([{ k: 'order' }, { k: 'what' }, { k: 'supplier' }, { k: 'project' }, { k: 'date' }, { k: 'total', right: true }],
                  tareas.antiguos.items.map(p => filaPedido(p)))}
              </>
            )
          )}

          {tarea === 'sin_proyecto' && (
            tareas.sin_proyecto.n === 0 ? <p className="text-sm text-gray-400 py-6 text-center">{t('tray.emptySinProyecto')}</p> :
              tabla([{ k: 'order' }, { k: 'what' }, { k: 'supplier' }, { k: 'project' }, { k: 'date' }, { k: 'noProject' }, { k: 'total', right: true }],
                tareas.sin_proyecto.items.map(p => filaPedido(p, <td className="px-3 py-2 text-amber-700 whitespace-nowrap">{t('tray.linesNoProject', { count: p.lineas_sin_proyecto, total: p.n_lineas })}</td>)))
          )}

          {tarea === 'sin_enlazar' && (
            tareas.sin_enlazar.n === 0 ? <p className="text-sm text-gray-400 py-6 text-center">{t('tray.emptySinEnlazar')}</p> : (
              <>
                {esAdmin && <Link to="/admin/proyectos" className="text-sm text-indigo-600 hover:underline">{t('banner.unlinkedGo')}</Link>}
                {tabla([{ k: 'sapCode' }, { k: 'sapName' }, { k: 'orders' }, { k: 'openOrders' }],
                  tareas.sin_enlazar.items.map(p => (
                    <tr key={p.codigo} className="border-t">
                      <td className="px-3 py-2 font-mono">{p.codigo}</td>
                      <td className="px-3 py-2">{p.nombre || '—'}</td>
                      <td className="px-3 py-2">{p.pedidos}</td>
                      <td className="px-3 py-2">{p.abiertos}</td>
                    </tr>
                  )))}
              </>
            )
          )}

          <p className="text-xs text-gray-400 pt-2">{t('tray.coming')}</p>
        </>
      )}
      </ZonaZoom>
      {!data && !err && <p className="text-sm text-gray-400">{t('loading')}</p>}

      {paquetes && <Paquetes paquetes={paquetes} onClose={() => setPaquetes(null)} t={t} />}
    </div>
  )
}

// One purchase per supplier: every approved request of it, ticked by default. Compras unticks what it will not
// buy now, or takes a request out for good ("Separar"); the requests keep their own project and approval.
function CompraConjunta({ c, fuera, alternar, ocupada, t, po, setPo, separando, setSeparando, separar, onPaquete, onCargadas }) {
  const n = c.solicitudes.filter(s => !fuera.has(s.id)).length
  return (
    <section className="bg-white rounded-xl border">
      <header className="px-4 py-3 border-b bg-gray-50 space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-medium text-gray-900">{c.proveedor || t('package.noSupplier')}</div>
            <div className="text-xs text-gray-500">
              {t('package.requestsCount', { count: c.n_solicitudes })} · {t('package.lines', { count: c.n_lineas })}
              {c.proyectos.length > 0 && <> · {c.proyectos.join(', ')}</>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={po} onChange={e => setPo(e.target.value)} placeholder={t('joint.poPlaceholder')} aria-label={t('joint.poPlaceholder')}
              className="w-56 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            <button type="button" onClick={onPaquete} disabled={!n || ocupada}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40">
              {t('joint.build', { count: n })}
            </button>
            <button type="button" onClick={onCargadas} disabled={!n || ocupada || !po.trim()} title={po.trim() ? undefined : t("joint.poRequired")}
              className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
              {t('joint.markLoaded', { count: n })}
            </button>
          </div>
        </div>
        {c.repetidos.length > 0 && (
          <div className="text-xs text-amber-700">
            {t('joint.repeated')} {c.repetidos.map(r => `${r.item} (${r.proyectos.join(' / ')})`).join('; ')}. {t('joint.repeatedHint')}
          </div>
        )}
        {c.por_aprobar.length > 0 && (
          <div className="text-xs text-sky-700">
            {t('joint.pending', { count: c.por_aprobar.length })} {c.por_aprobar.map(p => `#${p.id} ${p.proyecto || ''}`.trim()).join(', ')}
          </div>
        )}
      </header>
      <table className="w-full text-sm">
        <tbody>
          {c.solicitudes.map(s => (
            <Fragment key={s.id}>
              <tr className={`border-t first:border-t-0 ${fuera.has(s.id) ? 'opacity-50' : ''}`}>
                <td className="pl-3 py-2 w-8"><input type="checkbox" className="accent-gray-900" checked={!fuera.has(s.id)} onChange={() => alternar(s.id)} aria-label={t('package.markOne', { n: s.numero })} /></td>
                <td className="px-3 py-2 whitespace-nowrap"><a href={`/purchases/${s.id}`} target="_blank" rel="noreferrer" className="font-medium text-indigo-700 hover:underline">#{s.numero}</a>{s.sobre_corte != null && <span className={`ml-2 text-[10px] ${s.sobre_corte ? 'text-emerald-700' : 'text-gray-400'}`}>{s.sobre_corte ? t('joint.cut.above') : t('joint.cut.below')}</span>}</td>
                <td className="px-3 py-2 max-w-[22rem]">
                  <div className="truncate" title={s.lineas.map(l => l.descripcion).join(' · ')}>{s.lineas[0]?.descripcion}</div>
                  {s.lineas.length > 1 && <div className="text-xs text-gray-400">{t('moreLines', { n: s.lineas.length - 1 })}</div>}
                </td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{s.proyecto || '—'}{s.proyecto_sap && <span className="text-xs text-gray-400"> · {s.proyecto_sap}</span>}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{s.solicitante || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap"><Dias d={s.dias} t={t} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" disabled={ocupada} onClick={() => setSeparando({ id: s.id, motivo: '' })} className="text-xs text-gray-500 hover:text-gray-900 underline disabled:opacity-40">{t('joint.separate')}</button>
                </td>
              </tr>
              {separando?.id === s.id && (
                <tr className="bg-gray-50">
                  <td />
                  <td colSpan={6} className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <input autoFocus value={separando.motivo} onChange={e => setSeparando({ id: s.id, motivo: e.target.value })} placeholder={t('joint.reasonPlaceholder')}
                        className="flex-1 min-w-[16rem] px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                      <button type="button" disabled={separando.motivo.trim().length < 3 || ocupada} onClick={separar}
                        className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm disabled:opacity-40">{t('joint.separateConfirm')}</button>
                      <button type="button" onClick={() => setSeparando(null)} className="text-sm text-gray-500 underline">{t('package.close')}</button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  )
}
